import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { H_PIEZO_OPTIONS } from '../../../account/constants/AccessRulesConstants'
import Checkbox from '../../../components/forms/Checkbox'
import Select from '../../../components/forms/Select'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import NetworkAction from '../../../referencial/components/network/actions/NetworkAction'
import StationAction from '../../../station/actions/StationAction'
import { componentHasHabilitations } from '../../../utils/HabilitationUtil'
import { getGeneralLevelling, getSetting, getSettingBool, getSettingInt } from '../../../utils/SettingUtils'
import { getLabel } from '../../../utils/StoreUtils'
import AdministrationAction from '../../actions/AdministrationAction'
import DataTypesTable from '../dataTypes/DataTypesTable'
import ShowAssociatedDataRuleTable from '../dataTypes/ShowAssociatedDataRuleTable'
import RadioButtons from '../../../components/forms/RadioButtons'
import Button from '../../../components/forms/Button'
import FrequencyStatCalculationMethodPopup from './FrequencyStatCalculationMethodPopup'
import useTitle from 'utils/customHook/useTitle'
import { push } from '@lagunovsky/redux-react-router'
import useActions from 'utils/customHook/useActions'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { darkBlue } from 'utils/constants/ColorTheme'
import { omitBy, isObject, template } from 'lodash'

const project = 'SIES'

const frequencyCalculationMethods = [
    { code: 'normal', label: 'Loi normale' },
    { code: 'trunc', label: 'Loi normale tronquée' },
    { code: 'percent', label: 'Pourcentage par rapport à la moyenne' },
    { code: 'noLaw', label: 'Sans loi statistique' },
]

const AdminPiezometerApp = () => {
    const {
        applicationSettingsStore,
        contributors,
        filteredDataTypes,
        networks,
    } = useSelector(store => ({
        applicationSettingsStore: store.AdministrationReducer.applicationSettings,
        contributors: store.ContributorReducer.contributors,
        filteredDataTypes: store.StationReducer.filteredDataTypes,
        networks: store.NetworkReducer.networks,
    }), shallowEqual)

    const [applicationSettings, setApplicationSettings] = useState(applicationSettingsStore)
    const [readMode, setReadMode] = useState(true)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [frequencyCalculationPopupIsOpen, setFrequencyCalculationPopupIsOpen] = useState(false)
    const [dataTypes, setDataTypesState] = useState([])

    const dispatch = useDispatch()

    const setDataTypes = () => {
        dispatch(StationAction.fetchDataTypesByProject(project)).then((res) => {
            setDataLoaded(true)
            setDataTypesState(res)
        })
    }

    useEffect(() => {
        if (!componentHasHabilitations(H_PIEZO_OPTIONS)) { // A modifier quand react-router sera à jour
            dispatch(push('/unauthorized'))
            return
        }
        if (!networks.length) {
            dispatch(NetworkAction.fetchNetworks())
        }
        dispatch(ContributorAction.fetchContributors())
        setDataTypes()
    }, [])

    useTitle(() => [{
        title: i18n.piezometry,
        href: 'piezometry',
    }, {
        title: i18n.piezometryOptions,
        href: 'piezometry/piezometryOptions',
    }], [])

    const onCancel = () => {
        setDataTypes(filteredDataTypes)
        setApplicationSettings(applicationSettingsStore)
        setReadMode(true)
    }

    const onSave = () => {
        setDataLoaded(false)
        const applicationSettingsFiltered = applicationSettings.filter(e => e.value !== getSetting(applicationSettingsStore, e.parameter))
        if (applicationSettingsFiltered.length) {
            dispatch(AdministrationAction.updateSieauParameters(applicationSettingsFiltered))
        }
        dispatch(StationAction.updateDataTypesByProject(project, dataTypes.map(d => omitBy(d, isObject)))).then(() => {
            setDataTypes()
        })
        setReadMode(true)
    }

    useActions(() => {
        if (readMode) {
            return {
                edit: () => setReadMode(false),
            }
        }
        return {
            save: () => onSave(),
            cancel: () => onCancel(),
        }
    }, [readMode, applicationSettings, applicationSettingsStore, dataTypes])

    const onChangeSetting = (setting, value) => {
        const stateObj = applicationSettings.filter(e => e.parameter !== setting)
        setApplicationSettings([...stateObj, { parameter: setting, value: value ? value.toString() : value }])
    }

    const enveloppeCalculationOptions = [
        { value: 'false', label: i18n.calculateOnMaxData },
        { value: 'true', label: i18n.calculateOnRawData },
    ]

    const emailDepthOrNgfValues = [
        { value: 1, label: getGeneralLevelling() },
        { value: 2, label: i18n.depth },
    ]

    return (
        <Stack spacing={2}>
            <Card>
                <CardTitle title={i18n.piezometryOptions} color={darkBlue} />
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={5}>
                            <Select disabled={ readMode } label={ i18n.synchNetwork }
                                options={ networks }
                                value={ getSettingInt(applicationSettings, 'adesPiezoSynchroNetwork') }
                                onChange={ v => onChangeSetting('adesPiezoSynchroNetwork', v) } keyValue='id'
                                keyLabel='mnemonic'
                            />
                        </Grid>
                        <Grid size={7}>
                            <RadioButtons col={ 7 } title={ i18n.enveloppeStatCalculation }
                                selected={ getSetting(applicationSettings, 'CalculateEnveloppeOnRawData') || 'false' }
                                onChange={ v => onChangeSetting('CalculateEnveloppeOnRawData', v) }
                                elements={ enveloppeCalculationOptions } disabled={ readMode }
                            />
                        </Grid>
                        <Grid size={5}>
                            <Checkbox label='Activer les exports QESOUT' disabled={ readMode }
                                onChange={ v => onChangeSetting('QESOUTisActive', v.toString()) }
                                checked={ getSettingBool(applicationSettings, 'QESOUTisActive') ?? true }
                            />
                        </Grid>
                        <Grid size={3}>
                            <RadioButtons
                                title={ template(i18n.emailDepthOrNgf)({ value: getSetting(applicationSettings, 'GENERAL_LEVELLING') }) }
                                selected={ getSetting(applicationSettings, 'EMAIL_NGF_OR_DEPTH_VALUES') || 1 }
                                onChange={ v => onChangeSetting('EMAIL_NGF_OR_DEPTH_VALUES', v) }
                                elements={ emailDepthOrNgfValues }
                                disabled={ readMode }
                            />
                        </Grid>
                        <Grid size={12}>
                            <h5>{i18n.returnPeriodCalculationMethod}</h5>
                        </Grid>
                        <Grid size={12}>
                            <p>{i18n.returnPeriodCalculationMethodExplaination}</p>
                        </Grid>
                        <Grid size={3}>
                            <Button title={i18n.changeCalculationMethod} icon='edit'
                                onClick={ () => setFrequencyCalculationPopupIsOpen(true) }
                                disabled={ readMode }
                            />
                        </Grid>
                        <Grid size={9}>
                            <h6>{i18n.currentMethod}
                            : { getLabel(frequencyCalculationMethods, getSetting(applicationSettings, 'FrequencyStatMode') ?? 'normal') }</h6>
                        </Grid>
                        <Checkbox
                            label={i18n.showAllReturnPeriodsWithoutHistoCondition}
                            disabled={readMode}
                            onChange={ v => onChangeSetting('showAllReturnPeriodsPiezoWithoutHistoCondition', v.toString()) }
                            checked={ getSettingBool(applicationSettings, 'showAllReturnPeriodsPiezoWithoutHistoCondition') ?? false }
                        />
                        <Grid size={12}>
                            <h5>{ i18n.validationScreenDefaultValues }</h5>
                        </Grid>
                        <Grid size={4}>
                            <Select disabled={ readMode } label={ i18n.producer }
                                options={ contributors }
                                value={ getSettingInt(applicationSettings, 'validationDefaultProducer') }
                                onChange={ v => onChangeSetting('validationDefaultProducer', v) } keyValue='id'
                                keyLabel='mnemonique'
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select disabled={ readMode } label={ i18n.administrator }
                                options={ contributors }
                                value={ getSettingInt(applicationSettings, 'validationDefaultManager') }
                                onChange={ v => onChangeSetting('validationDefaultManager', v) } keyValue='id'
                                keyLabel='mnemonique'
                            />
                        </Grid>
                        <Grid size={4}>
                            <Select disabled={ readMode } label={ i18n.validator }
                                options={ contributors }
                                value={ getSetting(applicationSettings, 'validationDefaultValidator') }
                                onChange={ v => onChangeSetting('validationDefaultValidator', v) } keyValue='id'
                                keyLabel='mnemonique'
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            { dataLoaded && (
                <DataTypesTable
                    project={ project }
                    dataTypes={ dataTypes }
                    readMode={ readMode }
                    onChange={ (data) => setDataTypesState(data) }
                />
            ) }
            { dataLoaded && (
                <ShowAssociatedDataRuleTable
                    currentSettings={applicationSettings}
                    fromStationType='PIEZO'
                    toStationType='HYDRO'
                    onChangeRules={ rules => onChangeSetting('showDataTypesRules', rules) }
                    readMode={readMode}
                />
            )}
            {dataLoaded && (
                <ShowAssociatedDataRuleTable
                    currentSettings={applicationSettings}
                    fromStationType='PIEZO'
                    toStationType='PLUVIO'
                    onChangeRules={ rules => onChangeSetting('showDataTypesRules', rules) }
                    readMode={readMode}
                />
            )}
            <FrequencyStatCalculationMethodPopup
                selectedMode={getSetting(applicationSettings, 'FrequencyStatMode') ?? 'normal'}
                changeMode={mode => onChangeSetting('FrequencyStatMode', mode)}
                onClose={() => setFrequencyCalculationPopupIsOpen(false)}
                isOpen={frequencyCalculationPopupIsOpen}
            />
        </Stack>
    )
}

export default AdminPiezometerApp